import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  FaStar,
  FaMapMarkerAlt,
  FaTruck,
  FaSpinner,
  FaExclamationTriangle,
  FaStore,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { sellerStorefrontService } from '../services/sellerStorefrontService';
import { formatPrice } from '../utils/currency';
import { getPartDetailPath } from '../utils/partDetailRoutes';
import { useLocationPreference } from '../contexts/LocationPreferenceContext';

const TABS = [
  { key: 'all', label: 'All Parts' },
  { key: 'used', label: 'Used Parts' },
  { key: 'categories', label: 'Categories' },
];

function RatingDisplay({ rating, label }) {
  if (label === 'New seller' || rating == null) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
        New seller
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-sm font-semibold text-slate-900">
      <FaStar className="text-amber-400" />
      {Number(rating).toFixed(1)}
      <span className="font-normal text-slate-500">/ 5</span>
    </span>
  );
}

function ListingTile({ listing }) {
  const path = listing.catalogNodeId
    ? `/catalog/node/${listing.catalogNodeId}`
    : getPartDetailPath({
        id: listing.listingId,
        listingId: listing.listingId,
        name: listing.partName,
        condition: listing.condition,
      });

  return (
    <Link
      to={path}
      className="flex flex-col rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition hover:border-red-200 hover:shadow-md"
    >
      <div className="aspect-square w-full overflow-hidden rounded-lg bg-slate-100 mb-3">
        {listing.imageUrl ? (
          <img src={listing.imageUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-slate-400 text-xs">No image</div>
        )}
      </div>
      <p className="text-sm font-semibold text-slate-900 line-clamp-2 min-h-[2.5rem]">
        {listing.partName || 'Part'}
      </p>
      <p className="mt-1 text-lg font-bold text-red-600">{formatPrice(listing.price)}</p>
      {listing.condition && (
        <p className="mt-1 text-[11px] text-slate-500 capitalize">{listing.condition.replace(/_/g, ' ')}</p>
      )}
    </Link>
  );
}

export default function SellerStorefront() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const locationPref = useLocationPreference();
  const buyerPincode =
    locationPref?.registeredLocation?.pincode ||
    locationPref?.registeredLocation?.postalCode ||
    locationPref?.searchLocation?.pincode ||
    '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const tab = activeTab === 'categories' && selectedCategory ? 'all' : activeTab;
      const result = await sellerStorefrontService.getPublicStorefront(slug, {
        tab: tab === 'categories' ? 'all' : tab,
        category: selectedCategory || undefined,
        pincode: buyerPincode || undefined,
      });
      setData(result);
    } catch (err) {
      toast.error(err.message || 'Seller not found');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [slug, activeTab, selectedCategory, buyerPincode]);

  const profile = data?.profile;
  const listings = data?.listings || [];
  const categories = data?.categories || [];

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!profile) return;
    document.title = `${profile.shopName} | Sparepe Seller`;
    let meta = document.querySelector('meta[name="robots"]');
    if (!profile.seoIndexable) {
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'robots';
        document.head.appendChild(meta);
      }
      meta.content = 'noindex,nofollow';
    } else if (meta) {
      meta.remove();
    }
    return () => {
      document.title = 'Sparepe';
    };
  }, [profile]);

  const filteredListings = useMemo(() => {
    if (activeTab !== 'used') return listings;
    return listings.filter((l) => String(l.condition || '').includes('used'));
  }, [listings, activeTab]);

  const displayListings =
    activeTab === 'categories' && selectedCategory
      ? filteredListings.filter((l) =>
          String(l.partCategory || l.partName || '')
            .toLowerCase()
            .includes(selectedCategory.toLowerCase())
        )
      : filteredListings;

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-slate-500">
        <FaSpinner className="animate-spin mr-2" /> Loading storefront...
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <FaStore className="mx-auto text-4xl text-slate-300" />
        <h1 className="mt-4 text-xl font-bold text-slate-900">Seller not found</h1>
        <p className="mt-2 text-slate-500">This storefront may have been removed or is not yet public.</p>
        <Link to="/" className="mt-6 inline-block text-red-600 font-medium hover:underline">
          Back to home
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-4 sm:py-6 pb-24">
      <header className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
        <div className="flex gap-4">
          <div className="flex h-14 w-14 sm:h-16 sm:w-16 shrink-0 items-center justify-center rounded-full bg-red-600 text-lg sm:text-xl font-bold text-white">
            {profile.avatarInitials || 'S'}
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 truncate">{profile.shopName}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 sm:gap-3">
              <RatingDisplay rating={profile.rating} label={profile.ratingLabel} />
              <span className="inline-flex items-center gap-1 text-sm text-slate-600">
                <FaMapMarkerAlt className="text-slate-400 shrink-0" />
                {profile.locationDisplay}
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-500 inline-flex items-center gap-1">
              <FaTruck className="text-slate-400" />
              {profile.deliveryRadiusLabel}
            </p>
            {profile.responseRatePct != null && (
              <p className="mt-1 text-sm text-slate-600">
                Response rate: <strong>{profile.responseRatePct}%</strong>
              </p>
            )}
            {profile.lowResponseWarning && (
              <p className="mt-2 inline-flex items-center gap-1 rounded-lg bg-amber-50 border border-amber-200 px-2 py-1 text-xs text-amber-800">
                <FaExclamationTriangle />
                This seller has a low response rate
              </p>
            )}
          </div>
        </div>

        {profile.badges?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {profile.badges.map((badge) => (
              <span
                key={badge.id}
                className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700"
              >
                <span>{badge.icon}</span>
                {badge.label}
              </span>
            ))}
          </div>
        )}

        {!profile.withinRadius && buyerPincode && (
          <p className="mt-4 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-600">
            This seller does not deliver to your PIN code ({buyerPincode}).
          </p>
        )}
      </header>

      <nav className="mt-6 flex gap-1 overflow-x-auto border-b border-slate-200 pb-px scrollbar-hide">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => {
              setActiveTab(tab.key);
              if (tab.key !== 'categories') setSelectedCategory('');
            }}
            className={`shrink-0 px-4 py-2.5 text-sm font-medium border-b-2 transition ${
              activeTab === tab.key
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeTab === 'categories' && categories.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => {
                setSelectedCategory(cat);
                setSearchParams({ category: cat });
              }}
              className={`rounded-full px-3 py-1.5 text-xs font-medium border ${
                selectedCategory === cat
                  ? 'bg-red-600 text-white border-red-600'
                  : 'bg-white text-slate-600 border-slate-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      <section className="mt-6">
        {displayListings.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 py-16 text-center text-slate-500">
            No active listings in this section.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {displayListings.map((listing) => (
              <ListingTile key={listing.listingId} listing={listing} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
